import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { usableShelfDepth } from "./shelfLoader";

export const frontOverhangFits = (depth: number, shelfDepth: number, allowance: number) =>
  [depth, shelfDepth, allowance].every(Number.isFinite) &&
  shelfDepth > 0 &&
  allowance > 0 &&
  depth > shelfDepth &&
  depth - shelfDepth <= allowance &&
  5 * (depth - shelfDepth) <= depth;

export const frontOverhangAllowed = (item: PlanogramItem) =>
  item.merchandisingStyle === "UNIT" &&
  item.shelf.type === "REGULAR" &&
  item.shelf.slope === 0 &&
  !item.shelf.mergedLeft &&
  !item.shelf.mergedRight &&
  !item.zCappingFlag &&
  !(item.zCapNum && item.zCapNum > 0) &&
  frontOverhangFits(
    item.getDepth(),
    usableShelfDepth(item.shelf),
    item.shelf.maxFrontOverhang ?? 0,
  );

/** The POG's overhang policy: on once it is enabled or any shelf carries an allowance. */
export const frontOverhangPolicyOn = (pog: {
  frontOverhangEnabled?: boolean;
  planogram: { bays: { shelves: { maxFrontOverhang?: number }[] }[] };
}) =>
  !!pog.frontOverhangEnabled ||
  pog.planogram.bays.some((bay) => bay.shelves.some((shelf) => (shelf.maxFrontOverhang ?? 0) > 0));

/* A PSA carries the policy as a token in its Planogram record's custom data, so a file reopened
   as a new POG keeps validating after its last allowance is cleared. It only turns validation
   on; permission still comes from the store's fixtures. */
const FRONT_OVERHANG_POLICY_TOKEN = "OWA:frontOverhangPolicy";
const CUSTOM_DATA_SEPARATOR = ";";

export const psaCarriesFrontOverhangPolicy = (customData: string | null | undefined) =>
  (customData ?? "").split(CUSTOM_DATA_SEPARATOR).includes(FRONT_OVERHANG_POLICY_TOKEN);

/** The planner's own custom data, with the policy token added or removed and nothing else. */
export const psaCustomDataWithFrontOverhangPolicy = (
  customData: string | null | undefined,
  policyOn: boolean,
) => {
  const original = customData ?? "";
  if (psaCarriesFrontOverhangPolicy(original) === policyOn) return original;
  const parts = original.split(CUSTOM_DATA_SEPARATOR).filter((part) => part !== "");
  return (
    policyOn
      ? [...parts, FRONT_OVERHANG_POLICY_TOKEN]
      : parts.filter((part) => part !== FRONT_OVERHANG_POLICY_TOKEN)
  ).join(CUSTOM_DATA_SEPARATOR);
};

export interface FrontOverhangViolation {
  itemId: string;
  productCode: string;
  shelfId: string;
  message: string;
}

export const frontOverhangViolations = (pog: Pog): FrontOverhangViolation[] => {
  if (!frontOverhangPolicyOn(pog)) return [];
  return pog.planogram.bays.flatMap((bay) =>
    bay.shelves.flatMap((shelf) =>
      shelf.items.flatMap((item) => {
        const depth = item.getDepth();
        const shelfDepth = usableShelfDepth(shelf);
        if (depth > 0 && shelfDepth > 0 && depth <= shelfDepth) return [];
        if (frontOverhangAllowed(item) && item.getDepthFacings() === 1) return [];
        const excess = Math.max(0, depth - shelfDepth);
        return [
          {
            itemId: item.uniqueId,
            productCode: item.productCode,
            shelfId: shelf.uniqueId,
            message: `${item.name || item.productCode} on shelf ${bay.getNo()}.${shelf.getNo()} needs ${(excess / 100).toFixed(2)} inches of front overhang. The shelf allows ${((shelf.maxFrontOverhang ?? 0) / 100).toFixed(2)} inches; only one UNIT pack deep on a level, unmerged regular shelf, within 20% of its oriented depth, is permitted.`,
          },
        ];
      }),
    ),
  );
};

export const assertFrontOverhangValid = (pog: Pog) => {
  const violations = frontOverhangViolations(pog);
  if (violations.length) throw new Error(violations.map((v) => v.message).join("\n"));
};
